import React, { useMemo, useState } from 'react';
import { Container } from 'reactstrap';
import Layout from '../../templates/Layout';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_LEAK_TEST_PACK,
  GET_ALL_LEAK_TEST_PACKS,
  LINK_LEAK_TEST_PACK_JOBS,
  REMOVE_LEAK_TEST_PACK,
  UPDATE_LEAK_TEST_PACK,
} from '../../../graph-queries/tar/leakTestPack';
import { TLeakTestPack } from '../../../lib/types';
import { useIsFeatureFlagEnabled, useSelectedAsset } from '../../../lib/util';
import { useHasTarAdminAccess, useHasUserAccess } from '../../../lib/util/auth';
import AspectsTable from '../../organisms/AspectsTable/AspectsTable';
import LeakTestPackForm from '../../organisms/LeakTestPackForm/LeakTestPackForm';
import { ColumnDef } from '@tanstack/react-table';
import { authorCell, useAlertText } from '../../organisms/AspectsTable/util';
import {
  LEAK_TEST_PACK_REVIEW_ENUM,
  LEAK_TEST_PACK_TYPE_ENUM,
  LIFTPLAN_STATUS_ENUM,
} from '../../../lib/constants/dbConstants';
import { GET_ALL_EVENTS } from '../../../graph-queries/tar/event';
import LeakTestPackFiltersModal from '../../organisms/LeakTestPackFiltersModal/LeakTestPackFiltersModal';
import { EMPTY_LEAK_TEST_PACK_FILTERS } from '../../../lib/constants';
import { useBrowserLocationState } from '../../../lib/util/browser';
import { leakTestPacksGlobalFilterFn } from './LeakTestPack.util';

export const columns: ColumnDef<TLeakTestPack>[] = [
  {
    header: 'Title',
    accessorKey: 'leakTestPackTitle',
  },
  {
    header: 'Type',
    accessorKey: 'leakTestPackType',
    enableSorting: false,
    accessorFn: (row) => LEAK_TEST_PACK_TYPE_ENUM[row.leakTestPackType],
  },
  {
    header: 'Leak Test Pack Number',
    accessorKey: 'trackPackNumber',
    enableSorting: false,
  },
  {
    header: 'System Number',
    accessorKey: 'systemNumber',
    enableSorting: false,
  },
  {
    header: 'SPA',
    accessorKey: 'spa',
    enableSorting: false,
  },
  {
    header: 'Ops Review',
    accessorKey: 'opsReview',
    enableSorting: false,
    accessorFn: (row) => LEAK_TEST_PACK_REVIEW_ENUM[row.opsReview],
  },
  {
    header: 'Engineer Review',
    accessorKey: 'engineeringReview',
    enableSorting: false,
    accessorFn: (row) => LEAK_TEST_PACK_REVIEW_ENUM[row.engineeringReview],
  },
  {
    header: 'Asset Review',
    accessorKey: 'assetReview',
    enableSorting: false,
    accessorFn: (row) => LEAK_TEST_PACK_REVIEW_ENUM[row.assetReview],
  },
  {
    header: 'Status',
    accessorKey: 'status',
    enableSorting: false,
    accessorFn: (row) => LIFTPLAN_STATUS_ENUM[row.status],
  },
  {
    header: 'Comments',
    accessorKey: 'comments',
    enableSorting: false,
  },
  {
    header: 'Last Updated',
    accessorKey: 'updatedAt',
    cell: authorCell,
  },
];

const LeakTestPacksPage: React.FC = () => {
  const { alertText, setAlertText } = useAlertText();
  const hasUserAccess = useHasUserAccess();
  const hasTarAdminAccess = useHasTarAdminAccess();
  const asset = useSelectedAsset();
  const isFiltersEnabled = useIsFeatureFlagEnabled('FEATURE_FLAG_ENABLE_JOBS_FILTER');
  const [filters, setFilters] = useBrowserLocationState('filters', EMPTY_LEAK_TEST_PACK_FILTERS);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { data, loading } = useQuery(GET_ALL_LEAK_TEST_PACKS, {
    variables: { assetId: asset?.assetId || -1 },
    skip: !asset?.assetId,
  });
  const { data: events } = useQuery(GET_ALL_EVENTS);
  const leakTestPacksWithEvent = useMemo(() => {
    if (!data?.allLeakTestPacks || !events?.allEvents) return [];

    // Create a lookup map for events by ID
    const eventMap = new Map(events.allEvents.map((event) => [event.eventId, event]));

    return data.allLeakTestPacks.map((leakTestPack) => ({
      ...leakTestPack,
      tarJobs: leakTestPack?.tarJobs?.map((job) => ({
        ...job,
        tarEvent: job ? eventMap.get(job.eventId) : [],
      })),
    }));
  }, [data, events]);

  const [createLeakTestPack, { loading: isCreating }] = useMutation(CREATE_LEAK_TEST_PACK, {
    refetchQueries: [GET_ALL_LEAK_TEST_PACKS],
    awaitRefetchQueries: true,
  });
  const [updateLeakTestPack, { loading: isUpdating }] = useMutation(UPDATE_LEAK_TEST_PACK, {
    refetchQueries: [GET_ALL_LEAK_TEST_PACKS],
    awaitRefetchQueries: true,
  });
  const [linkLeakTestPackJobs, { loading: isLinking }] = useMutation(LINK_LEAK_TEST_PACK_JOBS, {
    refetchQueries: [GET_ALL_LEAK_TEST_PACKS],
    awaitRefetchQueries: true,
  });
  const [removeLeakTestPack, { loading: isRemoving }] = useMutation(REMOVE_LEAK_TEST_PACK, {
    refetchQueries: [GET_ALL_LEAK_TEST_PACKS],
    awaitRefetchQueries: true,
  });

  const onAddSubmit = (leakTestPack: TLeakTestPack) => {
    const { leakTestPackId, updatedBy, updatedAt, tarJobs, ...input } = leakTestPack;
    return createLeakTestPack({ variables: { input } }).then(() =>
      setAlertText('Leak test pack successfully added')
    );
  };

  const onEditSubmit = (leakTestPack: TLeakTestPack) => {
    const { updatedBy, updatedAt, tarJobs, ...input } = leakTestPack;
    return updateLeakTestPack({ variables: { input } }).then(() =>
      setAlertText('Leak test pack successfully updated')
    );
  };

  const onLinkSubmit = (selection: TLeakTestPack[], jobIds: number[]) => {
    return Promise.all(
      selection.map((leakTestPack) =>
        linkLeakTestPackJobs({
          variables: {
            leakTestPackId: leakTestPack.leakTestPackId,
            jobIds: jobIds,
          },
        })
      )
    ).then(() =>
      setAlertText(
        `Leak test pack successfully linked to ${jobIds.length} job${jobIds.length > 1 ? 's' : ''}`
      )
    );
  };

  const onRemoveSubmit = (selection: TLeakTestPack[]) => {
    return Promise.all(
      selection.map((leakTestPack) =>
        removeLeakTestPack({
          variables: { leakTestPackId: leakTestPack.leakTestPackId },
        })
      )
    );
  };

  const isLoading = isCreating || isUpdating || isLinking || isRemoving || loading || !!alertText;
  return (
    <Layout>
      <Container>
        {isFilterOpen && (
          <LeakTestPackFiltersModal
            defaultValues={filters}
            onSubmit={setFilters}
            onClose={() => setIsFilterOpen(false)}
          />
        )}
        {loading ? (
          'Loading...'
        ) : (
          <AspectsTable<TLeakTestPack>
            hideSelectionColumn
            aspectName="leak test pack"
            data={(leakTestPacksWithEvent as unknown as TLeakTestPack[]) || []}
            onLinkSubmit={onLinkSubmit}
            onRemoveSubmit={onRemoveSubmit}
            columns={columns}
            alertText={alertText}
            renderAddForm={(onClose) => (
              <LeakTestPackForm
                onSubmit={onAddSubmit}
                onClose={onClose}
                type={'add'}
                isLoading={isLoading}
              />
            )}
            renderEditForm={(onClose, defaultValue) => (
              <LeakTestPackForm
                defaultValues={defaultValue}
                onSubmit={onEditSubmit}
                onClose={onClose}
                type={'edit'}
                isLoading={isLoading}
              />
            )}
            creationDisabled={!hasUserAccess}
            getRowMutationDisabled={(_row) => !hasUserAccess}
            getRowDeletionDisabled={(_row) => !hasTarAdminAccess}
            searchEnabled={true}
            searchPlaceholderText="Search the leak test pack tracker..."
            isLoading={isLoading}
            globalFilterFn={leakTestPacksGlobalFilterFn}
            filters={filters}
            onFilterClick={(isFiltersEnabled && (() => setIsFilterOpen(true))) || undefined}
            onUpdateFilters={(newFilters) => setFilters(newFilters)}
          />
        )}
      </Container>
    </Layout>
  );
};

export default LeakTestPacksPage;
